<template>
  <div
    id="header"
    class="navbar navbar-expand-lg navbar-fixed navbar-height navbar-container navbar-bordered bg-white manager-header"
  >
    <div class="navbar-nav-wrap">
      <div class="navbar-nav-wrap-content-start">
        <button
          v-if="showMenu"
          type="button"
          class="manager-header__menu-toggle"
          :style="[componentValue ? { transform: 'rotate(90deg)' } : {}]"
          @click="change"
        >
          <i class="bi bi-list"></i>
        </button>

        <div class="manager-header__clock">
          <i v-if="nowDataTime" class="bi bi-calendar3"></i>
          <span>{{ nowDataTime }}</span>
        </div>
      </div>

      <div class="navbar-nav-wrap-content-end">
        <div class="manager-header__actions">
          <div class="manager-header__dropdown">
            <button
              ref="financeTrigger"
              type="button"
              class="manager-header__icon-button"
              @click="toggleMenu('finance')"
            >
              <i class="bi bi-wallet2"></i>
              <span v-if="financeBadgeCount" class="manager-header__badge manager-header__badge--warning">
                {{ toFaDigits(financeBadgeCount) }}
              </span>
            </button>

            <div v-if="financeMenuOpen" ref="financePanel" class="manager-header__panel">
              <div class="manager-header__panel-header">
                <div>
                  <p class="manager-header__panel-eyebrow">اعلان‌های مالی</p>
                  <h6 class="manager-header__panel-title">پرداخت‌ها، برداشت‌ها و معاملات</h6>
                </div>
                <button type="button" class="manager-header__close" @click="financeMenuOpen = false">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>

              <div class="manager-header__queue-grid">
                <button
                  v-for="card in financeQueueCards"
                  :key="card.key"
                  type="button"
                  class="queue-card"
                  @click="goToRoute(card.route)"
                >
                  <span class="queue-card__label">{{ card.label }}</span>
                  <strong>{{ toFaDigits(card.count) }}</strong>
                </button>
              </div>

              <div class="manager-header__panel-list">
                <div v-if="notificationsLoading && !financeNotifications.length" class="manager-header__empty">
                  در حال بارگذاری...
                </div>
                <div v-else-if="!financeNotifications.length" class="manager-header__empty">
                  فعلا اعلان مالی جدیدی وجود ندارد.
                </div>
                <template v-else>
                  <button
                    v-for="item in financeNotifications"
                    :key="item._id"
                    type="button"
                    class="manager-notification-card"
                    :class="{ 'manager-notification-card--unread': !item.isRead }"
                    @click="handleNotificationClick(item)"
                  >
                    <div class="manager-notification-card__icon" :class="notificationTypeClass(item.type)">
                      <i :class="notificationIcon(item)"></i>
                    </div>
                    <div class="manager-notification-card__content">
                      <div class="manager-notification-card__top">
                        <strong>{{ item.title || "اعلان جدید" }}</strong>
                        <span>{{ formatDate(item.createdAt) }}</span>
                      </div>
                      <p>{{ item.message }}</p>
                    </div>
                  </button>
                </template>
              </div>
            </div>
          </div>

          <div class="manager-header__dropdown">
            <button
              ref="ticketTrigger"
              type="button"
              class="manager-header__icon-button"
              @click="toggleMenu('ticket')"
            >
              <i class="bi bi-chat-dots"></i>
              <span v-if="ticketBadgeCount" class="manager-header__badge manager-header__badge--danger">
                {{ toFaDigits(ticketBadgeCount) }}
              </span>
            </button>

            <div v-if="ticketMenuOpen" ref="ticketPanel" class="manager-header__panel">
              <div class="manager-header__panel-header">
                <div>
                  <p class="manager-header__panel-eyebrow">اعلان‌های تیکت</p>
                  <h6 class="manager-header__panel-title">پیام‌های جدید کاربران و پشتیبانی</h6>
                </div>
                <button type="button" class="manager-header__close" @click="ticketMenuOpen = false">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>

              <div class="manager-header__ticket-summary">
                <div class="queue-card queue-card--wide">
                  <span class="queue-card__label">تیکت‌های دارای پیام جدید</span>
                  <strong>{{ toFaDigits(ticketBadgeCount) }}</strong>
                </div>
                <button type="button" class="manager-header__quick-link" @click="goToRoute({ name: 'PanelManager-Tickets' })">
                  مشاهده لیست تیکت‌ها
                </button>
              </div>

              <div class="manager-header__panel-list">
                <div v-if="notificationsLoading && !ticketNotifications.length" class="manager-header__empty">
                  در حال بارگذاری...
                </div>
                <div v-else-if="!ticketNotifications.length" class="manager-header__empty">
                  فعلا اعلان تیکت جدیدی وجود ندارد.
                </div>
                <template v-else>
                  <button
                    v-for="item in ticketNotifications"
                    :key="item._id"
                    type="button"
                    class="manager-notification-card"
                    :class="{ 'manager-notification-card--unread': !item.isRead }"
                    @click="handleNotificationClick(item)"
                  >
                    <div class="manager-notification-card__icon manager-notification-card__icon--ticket">
                      <i class="bi bi-chat-square-text"></i>
                    </div>
                    <div class="manager-notification-card__content">
                      <div class="manager-notification-card__top">
                        <strong>{{ item.title || "اعلان جدید" }}</strong>
                        <span>{{ formatDate(item.createdAt) }}</span>
                      </div>
                      <p>{{ item.message }}</p>
                    </div>
                  </button>
                </template>
              </div>
            </div>
          </div>

          <div class="manager-header__dropdown">
            <button
              ref="accountTrigger"
              type="button"
              class="manager-header__account"
              @click="toggleMenu('account')"
            >
              <div class="manager-header__avatar">
                <i class="bi bi-person"></i>
              </div>
              <i class="bi bi-chevron-down"></i>
            </button>

            <div v-if="accountMenuOpen" ref="accountPanel" class="manager-header__account-panel">
              <router-link class="manager-header__account-link" :to="{ name: 'PanelManager-Logout' }">
                {{ $t("sign_out") }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import jmoment from "jalali-moment";
import { toPersianDigits } from "@/utils/persianFormatter";

export default {
  props: ["modelValue", "showMenu"],
  emits: ["update:modelValue"],
  data() {
    return {
      nowDataTime: "",
      notificationsLoading: false,
      notificationItems: [],
      notificationSummary: {
        categoryCounters: {},
        managerQueues: {},
      },
      financeMenuOpen: false,
      ticketMenuOpen: false,
      accountMenuOpen: false,
      clockIntervalId: null,
      notificationIntervalId: null,
    };
  },
  computed: {
    componentValue: {
      get() {
        return this.modelValue;
      },
      set(componentValue) {
        this.$emit("update:modelValue", componentValue);
      },
    },
    managerQueues() {
      return this.notificationSummary.managerQueues || {};
    },
    ticketNotifications() {
      return this.notificationItems.filter((item) => item.category === "ticket").slice(0, 8);
    },
    financeNotifications() {
      return this.notificationItems.filter((item) => item.category !== "ticket").slice(0, 8);
    },
    ticketBadgeCount() {
      return Number(this.managerQueues.unreadManagerTickets || this.notificationSummary.categoryCounters?.ticket || 0);
    },
    financeBadgeCount() {
      return (
        Number(this.managerQueues.pendingManualPayments || 0) +
        Number(this.managerQueues.pendingWithdraws || 0) +
        Number(this.managerQueues.onlinePaymentsToday || 0) +
        Number(this.managerQueues.ordersInProgress || 0)
      );
    },
    financeQueueCards() {
      return [
        {
          key: "gateway",
          label: "پرداخت آنلاین امروز",
          count: this.managerQueues.onlinePaymentsToday || 0,
          route: { name: "PanelManager-Transactions-Gateway" },
        },
        {
          key: "manual-payment",
          label: "کارت به کارت در انتظار",
          count: this.managerQueues.pendingManualPayments || 0,
          route: { name: "PanelManager-ManualPayments", query: { status: "pending" } },
        },
        {
          key: "withdraw",
          label: "برداشت‌های فعال",
          count: this.managerQueues.pendingWithdraws || 0,
          route: { name: "PanelManager-Withdraws" },
        },
        {
          key: "order",
          label: "معاملات در انتظار",
          count: this.managerQueues.ordersInProgress || 0,
          route: { name: "PanelManager-Orders", query: { status: "IN-PROGRESS" } },
        },
      ];
    },
  },
  async mounted() {
    document.addEventListener("click", this.handleOutsideClick);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.getNow();
    this.clockIntervalId = window.setInterval(this.getNow, 1000);
    await this.refreshNotifications({ withList: false });
    this.notificationIntervalId = window.setInterval(() => {
      this.refreshNotifications({ withList: this.financeMenuOpen || this.ticketMenuOpen });
    }, 30000);
  },
  beforeUnmount() {
    if (this.clockIntervalId) {
      window.clearInterval(this.clockIntervalId);
    }
    if (this.notificationIntervalId) {
      window.clearInterval(this.notificationIntervalId);
    }
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
  },
  methods: {
    async refreshNotifications({ withList = false } = {}) {
      await this.fetchSummary();
      if (withList) {
        await this.fetchList();
      }
    },
    async fetchSummary() {
      try {
        const response = await axios.get("/v1/notifications/summary");
        this.notificationSummary = response?.data?.data || {
          categoryCounters: {},
          managerQueues: {},
        };
      } catch (_) {}
    },
    async fetchList() {
      try {
        this.notificationsLoading = true;
        const response = await axios.get("/v1/notifications", {
          params: {
            limit: 20,
          },
        });
        const data = response?.data?.data || {};
        this.notificationItems = Array.isArray(data.items) ? data.items : [];
      } catch (_) {
        this.notificationItems = [];
      } finally {
        this.notificationsLoading = false;
      }
    },
    getNow() {
      this.nowDataTime = jmoment(new Date()).locale("fa").format("D MMM YYYY ساعت HH:mm:ss");
    },
    change() {
      this.componentValue = !this.componentValue;
    },
    async toggleMenu(type) {
      const isCurrentOpen =
        (type === "finance" && this.financeMenuOpen) ||
        (type === "ticket" && this.ticketMenuOpen) ||
        (type === "account" && this.accountMenuOpen);

      this.closeAllMenus();

      if (isCurrentOpen) return;

      if (type === "finance") {
        this.financeMenuOpen = true;
        await this.fetchList();
      } else if (type === "ticket") {
        this.ticketMenuOpen = true;
        await this.fetchList();
      } else if (type === "account") {
        this.accountMenuOpen = true;
      }
    },
    closeAllMenus() {
      this.financeMenuOpen = false;
      this.ticketMenuOpen = false;
      this.accountMenuOpen = false;
    },
    async handleNotificationClick(item) {
      if (!item?._id) return;

      if (!item.isRead) {
        try {
          await axios.post(`/v1/notifications/${item._id}/read`);
          item.isRead = true;
        } catch (_) {}
      }

      this.closeAllMenus();
      await this.fetchSummary();

      if (item.route?.name) {
        this.$router.push({
          name: item.route.name,
          params: item.route.params || {},
          query: item.route.query || {},
        }).catch(() => {});
      }
    },
    goToRoute(route) {
      if (!route?.name) return;
      this.closeAllMenus();
      this.$router.push(route).catch(() => {});
    },
    notificationIcon(item) {
      const category = item?.category || "";
      if (category === "manual-payment") return "bi bi-credit-card";
      if (category === "withdraw") return "bi bi-cash-stack";
      if (category === "order") return "bi bi-arrow-left-right";
      return "bi bi-bell";
    },
    notificationTypeClass(type) {
      return {
        "manager-notification-card__icon--success": type === "success",
        "manager-notification-card__icon--warning": type === "warning",
        "manager-notification-card__icon--error": type === "error",
        "manager-notification-card__icon--info": !["success", "warning", "error"].includes(type),
      };
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("fa-IR", {
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    toFaDigits(value) {
      return toPersianDigits(value ?? "");
    },
    handleOutsideClick(event) {
      if (
        this.$refs.financePanel?.contains(event.target) ||
        this.$refs.financeTrigger?.contains(event.target) ||
        this.$refs.ticketPanel?.contains(event.target) ||
        this.$refs.ticketTrigger?.contains(event.target) ||
        this.$refs.accountPanel?.contains(event.target) ||
        this.$refs.accountTrigger?.contains(event.target)
      ) {
        return;
      }

      this.closeAllMenus();
    },
    handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        this.refreshNotifications({ withList: this.financeMenuOpen || this.ticketMenuOpen });
      }
    },
  },
};
</script>

<style scoped>
.manager-header {
  z-index: 1040;
}

.manager-header__menu-toggle,
.manager-header__icon-button,
.manager-header__close,
.manager-header__quick-link,
.queue-card {
  border: none;
  background: none;
}

.manager-header__menu-toggle {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(255, 193, 7, 0.08);
  color: #222;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  transition: 0.2s ease;
}

.manager-header__clock {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: #344054;
  font-size: 0.9rem;
}

.manager-header__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.manager-header__dropdown {
  position: relative;
}

.manager-header__icon-button,
.manager-header__account {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  color: #0f172a;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.manager-header__badge {
  position: absolute;
  top: -5px;
  left: -5px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.manager-header__badge--warning {
  background: #facc15;
  color: #111827;
}

.manager-header__badge--danger {
  background: #ef4444;
  color: #fff;
}

.manager-header__panel,
.manager-header__account-panel {
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  width: min(420px, calc(100vw - 2rem));
  border-radius: 24px;
  background: #fff;
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

.manager-header__account-panel {
  width: 180px;
  padding: 0.65rem;
}

.manager-header__panel-header {
  padding: 1rem 1rem 0.65rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.manager-header__panel-eyebrow {
  margin: 0 0 0.25rem;
  color: #64748b;
  font-size: 0.76rem;
}

.manager-header__panel-title {
  margin: 0;
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 800;
}

.manager-header__close {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(148, 163, 184, 0.12);
  color: #334155;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.manager-header__queue-grid {
  padding: 0 1rem 0.9rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.queue-card {
  text-align: right;
  padding: 0.95rem;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.08), rgba(59, 130, 246, 0.04));
  border: 1px solid rgba(255, 193, 7, 0.16);
  transition: transform 0.18s ease, border-color 0.18s ease;
}

.queue-card:hover {
  transform: translateY(-1px);
  border-color: rgba(255, 193, 7, 0.28);
}

.queue-card--wide {
  flex: 1;
}

.queue-card__label {
  display: block;
  color: #475569;
  font-size: 0.78rem;
  margin-bottom: 0.3rem;
}

.queue-card strong {
  color: #0f172a;
  font-size: 1.05rem;
}

.manager-header__ticket-summary {
  padding: 0 1rem 0.9rem;
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
}

.manager-header__quick-link {
  min-width: 132px;
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.04);
  color: #0f172a;
  font-weight: 700;
  padding: 0 1rem;
}

.manager-header__panel-list {
  max-height: 420px;
  overflow-y: auto;
  padding: 0 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.manager-header__empty {
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
}

.manager-notification-card {
  width: 100%;
  text-align: right;
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(248, 250, 252, 0.88);
  border-radius: 18px;
  padding: 0.95rem;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;
}

.manager-notification-card:hover {
  transform: translateY(-1px);
  border-color: rgba(255, 193, 7, 0.24);
  background: #fff;
}

.manager-notification-card--unread {
  border-color: rgba(255, 193, 7, 0.28);
  box-shadow: inset 0 0 0 1px rgba(255, 193, 7, 0.08);
}

.manager-notification-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.manager-notification-card__icon--ticket {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.manager-notification-card__icon--info {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.manager-notification-card__icon--success {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.manager-notification-card__icon--warning {
  background: rgba(255, 193, 7, 0.14);
  color: #ca8a04;
}

.manager-notification-card__icon--error {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.manager-notification-card__content {
  flex: 1;
  min-width: 0;
}

.manager-notification-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.35rem;
}

.manager-notification-card__top strong {
  color: #0f172a;
  font-size: 0.9rem;
}

.manager-notification-card__top span,
.manager-notification-card__content p {
  color: #64748b;
  font-size: 0.8rem;
}

.manager-notification-card__content p {
  margin: 0;
  line-height: 1.9;
}

.manager-header__account {
  width: auto;
  padding: 0 0.8rem;
  gap: 0.45rem;
}

.manager-header__avatar {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: rgba(255, 193, 7, 0.12);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.manager-header__account-link {
  display: block;
  text-decoration: none;
  color: #0f172a;
  border-radius: 14px;
  padding: 0.7rem 0.85rem;
}

.manager-header__account-link:hover {
  background: rgba(148, 163, 184, 0.1);
}

@media (max-width: 768px) {
  .manager-header__clock {
    display: none;
  }

  .manager-header__actions {
    gap: 0.55rem;
  }

  .manager-header__panel,
  .manager-header__account-panel {
    position: fixed;
    top: 72px;
    left: 1rem;
    width: calc(100vw - 2rem);
  }

  .manager-header__queue-grid {
    grid-template-columns: 1fr;
  }

  .manager-header__ticket-summary {
    flex-direction: column;
  }
}
</style>
